import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, Trash2, BarChart3, X, Download } from 'lucide-react';

type Coupon = {
  id: string;
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  is_active: boolean;
  uses_remaining: number | null;
  valid_from: string | null;
  valid_until: string | null;
};

type Enquiry = {
  id: string;
  name: string;
  email: string;
  apartment: string;
  checkin: string;
  checkout: string;
  total_amount: number | null;
  discount_amount: number | null;
  total_nights: number | null;
  status: string;
  created_at: string;
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount_type: 'percentage' as 'fixed' | 'percentage', discount_value: 0, uses_remaining: null as number | null, valid_from: '', valid_until: '' });
  const [reportCode, setReportCode] = useState<string | null>(null);
  const [reportData, setReportData] = useState<Enquiry[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('coupons').select('*').order('created_at', { ascending: false });
    setCoupons((data as Coupon[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code) { toast.error('Enter a coupon code'); return; }
    const { error } = await supabase.from('coupons').insert({
      code: form.code.toUpperCase(),
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      uses_remaining: form.uses_remaining,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
    });
    if (error) { toast.error('Failed to create: ' + error.message); return; }
    toast.success('Coupon created');
    setShowForm(false);
    setForm({ code: '', discount_type: 'percentage', discount_value: 0, uses_remaining: null, valid_from: '', valid_until: '' });
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from('coupons').delete().eq('id', id);
    if (error) { toast.error('Failed to delete: ' + error.message); return; }
    toast.success('Coupon deleted');
    load();
  };

  const toggle = async (id: string, active: boolean) => {
    const { error } = await supabase.from('coupons').update({ is_active: !active }).eq('id', id);
    if (error) { toast.error('Failed to update: ' + error.message); return; }
    load();
  };

  const openReport = async (code: string) => {
    setReportCode(code);
    setReportLoading(true);
    const { data, error } = await supabase
      .from('enquiries')
      .select('*')
      .eq('coupon_code', code)
      .order('created_at', { ascending: false });
    if (error) { toast.error('Failed to load report: ' + error.message); }
    setReportData((data as Enquiry[]) ?? []);
    setReportLoading(false);
  };

  const totalDiscountGiven = reportData.reduce((sum, e) => sum + (e.discount_amount ?? 0), 0);
  const totalRevenue = reportData.reduce((sum, e) => sum + (e.total_amount ?? 0), 0);
  const acceptedCount = reportData.filter(e => e.status === 'accepted').length;

  const exportCSV = () => {
    if (reportData.length === 0) return;
    const headers = ['Name', 'Email', 'Apartment', 'Check-in', 'Check-out', 'Nights', 'Total (€)', 'Discount (€)', 'Status', 'Date'];
    const rows = reportData.map(e => [
      e.name, e.email, e.apartment, e.checkin, e.checkout,
      e.total_nights ?? '', e.total_amount ?? '', e.discount_amount ?? '',
      e.status, new Date(e.created_at).toLocaleDateString()
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coupon-report-${reportCode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-light">Coupons</h1>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 text-sm tracking-wider uppercase rounded-sm hover:bg-accent/90">
          <Plus size={16} /> Add Coupon
        </button>
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">Code</label>
              <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent uppercase" placeholder="e.g. SUMMER25" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">Type</label>
              <select value={form.discount_type} onChange={e => setForm(p => ({ ...p, discount_type: e.target.value as 'fixed' | 'percentage' }))}
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed (€)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground tracking-wider uppercase mb-1 block">Value</label>
              <input type="number" min={0} value={form.discount_value} onChange={e => setForm(p => ({ ...p, discount_value: Number(e.target.value) }))}
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-accent" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="bg-accent text-accent-foreground px-6 py-2 text-sm tracking-wider uppercase rounded-sm hover:bg-accent/90">Create</button>
            <button onClick={() => setShowForm(false)} className="px-6 py-2 text-sm text-muted-foreground border border-border rounded-sm hover:bg-muted">Cancel</button>
          </div>
        </div>
      )}

      {/* Coupon Report Modal */}
      {reportCode && (
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-serif text-xl">Report: <code className="font-mono font-semibold">{reportCode}</code></h3>
              <p className="text-sm text-muted-foreground mt-1">{reportData.length} enquiries found</p>
            </div>
            <div className="flex gap-2">
              {reportData.length > 0 && (
                <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-1.5 text-xs border border-border rounded-sm hover:bg-muted">
                  <Download size={14} /> Export CSV
                </button>
              )}
              <button onClick={() => { setReportCode(null); setReportData([]); }} className="p-2 rounded-sm hover:bg-muted">
                <X size={16} />
              </button>
            </div>
          </div>

          {reportLoading ? (
            <p className="text-center text-muted-foreground py-8">Loading...</p>
          ) : reportData.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No enquiries have used this coupon yet</p>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-secondary/50 rounded-sm p-4">
                  <p className="text-xs text-muted-foreground tracking-wider uppercase mb-1">Total Uses</p>
                  <p className="font-serif text-2xl font-light">{reportData.length}</p>
                </div>
                <div className="bg-secondary/50 rounded-sm p-4">
                  <p className="text-xs text-muted-foreground tracking-wider uppercase mb-1">Accepted</p>
                  <p className="font-serif text-2xl font-light">{acceptedCount}</p>
                </div>
                <div className="bg-secondary/50 rounded-sm p-4">
                  <p className="text-xs text-muted-foreground tracking-wider uppercase mb-1">Total Discount</p>
                  <p className="font-serif text-2xl font-light">€{totalDiscountGiven.toFixed(0)}</p>
                </div>
                <div className="bg-secondary/50 rounded-sm p-4">
                  <p className="text-xs text-muted-foreground tracking-wider uppercase mb-1">Total Revenue</p>
                  <p className="font-serif text-2xl font-light">€{totalRevenue.toFixed(0)}</p>
                </div>
              </div>

              {/* Enquiry table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Guest</th>
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Apartment</th>
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Dates</th>
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Nights</th>
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Discount</th>
                      <th className="pb-2 pr-4 text-xs text-muted-foreground tracking-wider uppercase font-medium">Total</th>
                      <th className="pb-2 text-xs text-muted-foreground tracking-wider uppercase font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map(e => (
                      <tr key={e.id} className="border-b border-border/50">
                        <td className="py-3 pr-4">
                          <p className="font-medium">{e.name}</p>
                          <p className="text-xs text-muted-foreground">{e.email}</p>
                        </td>
                        <td className="py-3 pr-4">{e.apartment}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">{e.checkin} → {e.checkout}</td>
                        <td className="py-3 pr-4">{e.total_nights ?? '—'}</td>
                        <td className="py-3 pr-4">€{e.discount_amount ?? 0}</td>
                        <td className="py-3 pr-4">€{e.total_amount ?? 0}</td>
                        <td className="py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            e.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            e.status === 'declined' ? 'bg-red-100 text-red-800' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid gap-3">
        {coupons.map(c => (
          <div key={c.id} className="bg-card border border-border rounded-sm p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <code className="font-mono text-lg font-semibold">{c.code}</code>
                <span className={`text-xs px-2 py-0.5 rounded-sm ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `€${c.discount_value} off`}
                {c.uses_remaining != null && ` · ${c.uses_remaining} uses left`}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openReport(c.code)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-sm hover:bg-muted" title="View Report">
                <BarChart3 size={14} /> Report
              </button>
              <button onClick={() => toggle(c.id, c.is_active)} className="px-3 py-1.5 text-xs border border-border rounded-sm hover:bg-muted">
                {c.is_active ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => remove(c.id)} className="p-2 rounded-sm bg-red-50 text-red-600 hover:bg-red-100"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {coupons.length === 0 && <p className="text-center text-muted-foreground py-12">No coupons yet</p>}
      </div>
    </div>
  );
}
